// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { Form, Message } from 'semantic-ui-react';
import {
  SUPPORTED_COUNTRIES,
  getCountryConfig,
  normalizePostalCode,
  validatePostalCode
} from '../config/addressConfig';

/**
 * Address data structure matching ISO 19160 format
 */
export interface AddressData {
  addressLine1: string;
  addressLine2?: string;
  addressLine3?: string;
  townCity: string;
  stateProvinceCounty?: string;
  postalCode: string;
  countryCode: string;
  country: string;
}

interface AddressFormProps {
  /** Current address data */
  address: AddressData;
  /** Called when any address field changes */
  onChange: (field: keyof AddressData, value: string) => void;
  /** Validation errors for address fields */
  errors?: Record<string, string>;
  /** Whether the form is in read-only mode */
  disabled?: boolean;
  /** Whether all fields are optional (for entities where address is not required) */
  allOptional?: boolean;
}

/**
 * AddressForm Component
 *
 * Reusable address form component with international address support.
 * Supports Ireland (Eircode), UK (Postcode), and Canada (Postal Code) formats.
 *
 * Features:
 * - Dynamic field labels based on selected country
 * - Country-specific postal code validation
 * - Automatic postal code formatting
 * - Optional address line 2 and 3 fields
 * - Optional state/province/county field
 */
export function AddressForm({ address, onChange, errors = {}, disabled = false, allOptional = false }: Readonly<AddressFormProps>) {
  const config = getCountryConfig(address.countryCode || 'IRL');

  const handleCountryChange = (countryCode: string) => {
    const country = SUPPORTED_COUNTRIES.find(c => c.value === countryCode);
    if (country) {
      onChange('countryCode', countryCode);
      onChange('country', country.text);
      // Clear postal code when country changes as format will be different
      onChange('postalCode', '');
    }
  };

  const handlePostalCodeChange = (value: string) => {
    // Auto-format postal code as user types
    const normalized = normalizePostalCode(address.countryCode, value);
    onChange('postalCode', normalized);
  };

  const handlePostalCodeBlur = () => {
    // Validate postal code on blur
    if (address.postalCode && !validatePostalCode(address.countryCode, address.postalCode)) {
      // Let parent component handle validation error display
    }
  };

  return (
    <>
      <Form.Select
        label={allOptional ? 'Country (Optional)' : 'Country'}
        placeholder='Select country'
        options={SUPPORTED_COUNTRIES}
        value={address.countryCode}
        onChange={(_, data) => handleCountryChange(data.value as string)}
        error={errors.country || errors.countryCode ? { content: errors.country || errors.countryCode } : undefined}
        disabled={disabled}
        required={!allOptional}
      />

      <Form.Input
        label={allOptional ? 'Address Line 1 (Optional)' : 'Address Line 1'}
        placeholder='Street address, P.O. box'
        value={address.addressLine1}
        onChange={(e) => onChange('addressLine1', e.target.value)}
        error={errors.addressLine1 ? { content: errors.addressLine1 } : undefined}
        disabled={disabled}
        required={!allOptional}
      />

      <Form.Input
        label='Address Line 2 (Optional)'
        placeholder='Apartment, suite, unit, building, floor, etc.'
        value={address.addressLine2 || ''}
        onChange={(e) => onChange('addressLine2', e.target.value)}
        error={errors.addressLine2 ? { content: errors.addressLine2 } : undefined}
        disabled={disabled}
      />

      <Form.Input
        label='Address Line 3 (Optional)'
        placeholder='Additional address information'
        value={address.addressLine3 || ''}
        onChange={(e) => onChange('addressLine3', e.target.value)}
        error={errors.addressLine3 ? { content: errors.addressLine3 } : undefined}
        disabled={disabled}
      />

      <Form.Group widths='equal'>
        <Form.Input
          label={allOptional ? `${config.townCityLabel} (Optional)` : config.townCityLabel}
          placeholder={config.townCityPlaceholder}
          value={address.townCity}
          onChange={(e) => onChange('townCity', e.target.value)}
          error={errors.townCity ? { content: errors.townCity } : undefined}
          disabled={disabled}
          required={!allOptional}
        />
        <Form.Input
          label={`${config.stateProvinceLabel}${allOptional || !config.stateProvinceRequired ? ' (Optional)' : ''}`}
          placeholder={config.stateProvincePlaceholder}
          value={address.stateProvinceCounty || ''}
          onChange={(e) => onChange('stateProvinceCounty', e.target.value)}
          error={errors.stateProvinceCounty ? { content: errors.stateProvinceCounty } : undefined}
          disabled={disabled}
          required={!allOptional && config.stateProvinceRequired}
        />
      </Form.Group>

      <Form.Input
        label={allOptional ? `${config.postalCodeLabel} (Optional)` : config.postalCodeLabel}
        placeholder={config.postalCodePlaceholder}
        value={address.postalCode}
        onChange={(e) => handlePostalCodeChange(e.target.value)}
        onBlur={handlePostalCodeBlur}
        error={errors.postalCode ? { content: errors.postalCode } : undefined}
        disabled={disabled}
        required={!allOptional}
      />

      {errors.postalCode && (
        <Message size='tiny' warning>
          <Message.Header>Expected Format</Message.Header>
          <p>{config.postalCodeFormat}</p>
        </Message>
      )}
    </>
  );
}
